import { useEffect, useRef } from "react";
import { api } from "../../lib/api";

/**
 * Injects admin-editable HTML (from SiteSettings.custom_head_html and
 * custom_body_html) into <head> and just before </body>.
 *
 * Behaviour:
 *  - Fetches settings once on mount.
 *  - Uses a wrapper <div> so React-managed DOM remains untouched.
 *  - Executes any inline <script> tags by re-creating them (browsers do not
 *    run <script> that got there via innerHTML).
 *  - On unmount / settings change, removes previously-injected nodes so we
 *    never end up with duplicates during hot-reload or SPA route changes.
 *
 * NEVER call this from the CMS/admin routes.
 */
export default function CustomScripts() {
  const injectedHeadRef = useRef(null);
  const injectedBodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const cleanup = () => {
      if (injectedHeadRef.current) {
        injectedHeadRef.current.remove();
        injectedHeadRef.current = null;
      }
      if (injectedBodyRef.current) {
        injectedBodyRef.current.remove();
        injectedBodyRef.current = null;
      }
    };

    const injectInto = (parent, html, placement) => {
      if (!html || !String(html).trim()) return null;
      const wrapper = document.createElement("div");
      wrapper.setAttribute("data-cecit-custom", placement);
      wrapper.style.display = "contents";
      wrapper.innerHTML = html;

      // <script> tags inserted via innerHTML are inert. Recreate each one so
      // the browser actually executes them.
      wrapper.querySelectorAll("script").forEach((oldScript) => {
        const s = document.createElement("script");
        for (const attr of oldScript.attributes) s.setAttribute(attr.name, attr.value);
        s.text = oldScript.textContent || "";
        oldScript.parentNode.replaceChild(s, oldScript);
      });

      parent.appendChild(wrapper);
      return wrapper;
    };

    api.get("/settings").then((r) => {
      if (cancelled) return;
      cleanup();
      injectedHeadRef.current = injectInto(document.head, r.data?.custom_head_html || "", "head");
      injectedBodyRef.current = injectInto(document.body, r.data?.custom_body_html || "", "body");
    }).catch(() => {});

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);

  return null;
}
